import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import AccountMenu from './AccountMenu';

/* Desktop sidebar navigation. Rendered once by App.js (outside <Routes>),
   shown only at >=1024px via global.css. Hides itself on full-bleed routes
   and toggles `body.has-sidebar`. */

const HIDE_ON = ['/login', '/auth', '/claim', '/setup', '/privacy', '/terms', '/text-alerts'];

function IconHome() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" /></svg>);
}
function IconTrends() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 17 9 11l4 4 8-9" /><path d="M14 6h7v7" /></svg>);
}
function IconAlerts() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" /><path d="M9 18a3 3 0 0 0 6 0" /></svg>);
}
function IconSettings() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>);
}
function IconBook() {
  return (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19V5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 0-2 2" /><path d="M19 17H6a2 2 0 0 0-2 2" /></svg>);
}

// Order mirrors the mobile bottom nav: Home · Trends · Settings · Alerts.
const NAV = [
  { path: '/dashboard', label: 'Dashboard', Icon: IconHome,     tour: 'nav-home' },
  { path: '/trends',    label: 'Trends',    Icon: IconTrends,   tour: 'nav-trends' },
  { path: '/settings',  label: 'Settings',  Icon: IconSettings, tour: 'nav-settings' },
  { path: '/alerts',    label: 'Alerts',    Icon: IconAlerts,   tour: 'nav-alerts' },
];

export default function Sidebar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const hidden = HIDE_ON.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  useEffect(() => {
    document.body.classList.toggle('has-sidebar', !hidden);
    return () => document.body.classList.remove('has-sidebar');
  }, [hidden]);

  if (hidden) return null;

  const isActive = (path) => location.pathname === path;

  return (
    <aside className="sidebar" aria-label="Primary">
      <button type="button" className="sidebar-brand" onClick={() => navigate('/dashboard')} aria-label="SoKo Water Systems home">
        <Logo variant="icon" width={34} />
        <span className="sidebar-brand-text">
          <span className="name">SoKo</span>
          <span className="desc">Water Systems</span>
        </span>
      </button>

      <nav className="sidebar-nav">
        {NAV.map(({ path, label, Icon, tour }) => (
          <button
            key={path}
            type="button"
            className={`sidebar-link ${isActive(path) ? 'active' : ''}`}
            onClick={() => navigate(path)}
            aria-current={isActive(path) ? 'page' : undefined}
            data-tour={tour}
          >
            <span className="sidebar-link-icon"><Icon /></span>
            {label}
          </button>
        ))}

        <div className="sidebar-section-label">Help</div>
        <button
          type="button"
          className={`sidebar-link ${isActive('/help') ? 'active' : ''}`}
          onClick={() => navigate('/help')}
          aria-current={isActive('/help') ? 'page' : undefined}
          data-tour="nav-help"
        >
          <span className="sidebar-link-icon"><IconBook /></span>
          User manual
        </button>
      </nav>

      <div className="sidebar-bottom">
        <ThemeToggle withLabel className="theme-toggle--sidebar" />
        <AccountMenu variant="chip" user={user} />
      </div>
    </aside>
  );
}
